import React, {MouseEvent, RefObject, useCallback, useRef, useState} from "react"
import {LinkPosition} from "@dv5/domain"
import {MousePosition} from "../model/MousePosition"
import {ResizeDirection} from "../model/ResizeDirection"
import {WidgetEditorProps} from "../model/WidgetProps"
import {RESIZE_HANDLE_SIZE} from "../containers/utils/constants"

export interface WidgetEditorHandlers {
    onMouseDown: (e: MouseEvent<HTMLDivElement>) => void
    onMouseMove: (e: MouseEvent<HTMLDivElement>) => void
    onContextMenu: (e: MouseEvent<HTMLDivElement>) => void
    onMouseEnter: () => void
    onMouseLeave: () => void
}

interface UseWidgetEditorReturn {
    containerRef: RefObject<HTMLDivElement | null>
    resizeCursor: string
    isHovered: boolean
    handlers: WidgetEditorHandlers
    renderResizeHandles: () => React.ReactElement
    renderLinkDots: () => React.ReactElement
    handleLinkDotClick: (position: LinkPosition) => void
    handleResizeMouseDown: (e: MouseEvent<HTMLDivElement>, direction: ResizeDirection) => void
}

const getResizeDirection = (x: number, y: number, w: number, h: number): ResizeDirection | null => {
    const handleSize = RESIZE_HANDLE_SIZE
    if (x < handleSize && y < handleSize) return ResizeDirection.NW
    if (x > w - handleSize && y < handleSize) return ResizeDirection.NE
    if (x < handleSize && y > h - handleSize) return ResizeDirection.SW
    if (x > w - handleSize && y > h - handleSize) return ResizeDirection.SE
    if (x < handleSize) return ResizeDirection.W
    if (x > w - handleSize) return ResizeDirection.E
    if (y < handleSize) return ResizeDirection.N
    if (y > h - handleSize) return ResizeDirection.S
    return null
}

const getCursorForDirection = (direction: ResizeDirection): string => {
    const cursorMap: Record<ResizeDirection, string> = {
        [ResizeDirection.N]: 'n-resize',
        [ResizeDirection.S]: 's-resize',
        [ResizeDirection.E]: 'e-resize',
        [ResizeDirection.W]: 'w-resize',
        [ResizeDirection.NE]: 'ne-resize',
        [ResizeDirection.NW]: 'nw-resize',
        [ResizeDirection.SE]: 'se-resize',
        [ResizeDirection.SW]: 'sw-resize',
    }
    return cursorMap[direction]
}

export function useWidgetEditor({
    widget,
    selected,
    editingWidgetId,
    dragState,
    onStartDrag,
    onStartResize,
    onContextMenu,
    onStartLinkCreation,
    onCompleteLinkCreation,
}: WidgetEditorProps): UseWidgetEditorReturn {
    const containerRef = useRef<HTMLDivElement>(null)
    const [resizeCursor, setResizeCursor] = useState<string>('default')
    const [isHovered, setIsHovered] = useState(false)

    const handleMouseMove = useCallback((e: MouseEvent<HTMLDivElement>) => {
        if (editingWidgetId || !selected || widget.locked) {
            setResizeCursor('default')
            return
        }
        const rect = containerRef.current?.getBoundingClientRect()
        if (!rect) return

        const x = e.clientX - rect.left
        const y = e.clientY - rect.top
        const direction = getResizeDirection(x, y, rect.width, rect.height)

        if (direction) {
            setResizeCursor(getCursorForDirection(direction))
        } else {
            setResizeCursor('default')
        }
    }, [editingWidgetId, selected, widget.locked])

    const handleResizeMouseDown = useCallback((e: MouseEvent<HTMLDivElement>, direction: ResizeDirection) => {
        if (widget.locked) {
            return
        }
        e.stopPropagation()
        e.preventDefault()
        const position: MousePosition = {x: e.clientX, y: e.clientY}
        onStartResize?.(widget.id, position, widget.left, widget.top, widget.width, widget.height, direction)
    }, [widget, onStartResize])

    const handleMouseDown = useCallback((e: MouseEvent<HTMLDivElement>) => {
        if (editingWidgetId) {
            return
        }
        if (widget.locked) {
            return
        }
        if (e.button !== 0) {
            return
        }
        if (resizeCursor !== 'default') {
            return
        }
        e.stopPropagation()
        const position: MousePosition = {x: e.clientX, y: e.clientY}
        onStartDrag(widget.id, position, widget.left, widget.top)
    }, [editingWidgetId, resizeCursor, widget, onStartDrag])

    const handleContextMenu = useCallback((e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault()
        onContextMenu(e, widget.id)
    }, [widget, onContextMenu])

    const handleLinkDotClick = useCallback((position: LinkPosition) => {
        if (dragState.linkCreation) {
            onCompleteLinkCreation(widget.id, position)
        } else {
            onStartLinkCreation(widget.id, position)
        }
    }, [widget, dragState, onStartLinkCreation, onCompleteLinkCreation])

    const renderResizeHandles = useCallback(() => {
        if (editingWidgetId || !selected || widget.locked) {
            return <></>
        }
        const handleClass = (direction: ResizeDirection) => `resize-handle resize-handle-${direction}`
        return (
            <>
                <div className={handleClass(ResizeDirection.N)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.N)}/>
                <div className={handleClass(ResizeDirection.S)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.S)}/>
                <div className={handleClass(ResizeDirection.E)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.E)}/>
                <div className={handleClass(ResizeDirection.W)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.W)}/>
                <div className={handleClass(ResizeDirection.NE)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.NE)}/>
                <div className={handleClass(ResizeDirection.NW)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.NW)}/>
                <div className={handleClass(ResizeDirection.SE)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.SE)}/>
                <div className={handleClass(ResizeDirection.SW)}
                     onMouseDown={(e) => handleResizeMouseDown(e, ResizeDirection.SW)}/>
            </>
        )
    }, [editingWidgetId, selected, widget.locked, handleResizeMouseDown])

    const renderLinkDots = useCallback(() => {
        if (editingWidgetId || widget.locked) {
            return <></>
        }
        const positions: LinkPosition[] = ['top', 'right', 'bottom', 'left']
        return (
            <>
                {positions.map(position => (
                    <div
                        key={position}
                        className={`link-dot link-dot-${position}`}
                        onClick={() => handleLinkDotClick(position)}
                    />
                ))}
            </>
        )
    }, [editingWidgetId, widget.locked, handleLinkDotClick])

    const handlers: WidgetEditorHandlers = {
        onMouseDown: handleMouseDown,
        onMouseMove: handleMouseMove,
        onContextMenu: handleContextMenu,
        onMouseEnter: () => setIsHovered(true),
        onMouseLeave: () => setIsHovered(false),
    }

    return {
        containerRef,
        resizeCursor,
        isHovered,
        handlers,
        renderResizeHandles,
        renderLinkDots,
        handleLinkDotClick,
        handleResizeMouseDown,
    }
}
